"use client";

import React from "react";
import { usePostData } from "@/views/post/context/PostDataContext";
import type { Media } from "@/payload-types";
import type { LocaleCode } from "@/lib/labels/types";
import { VideoEmbed } from "../VideoEmbed/VideoEmbed";

interface VideoHeaderProps {
    /**
     * Optional breadcrumb element rendered inside the hero, above the title row.
     */
    breadcrumbs?: React.ReactNode;
}

/**
 * Content-only video hero. The branded background is provided by the wrapping
 * SectionStrip in the layout (VIDEO_HERO section slot, falling back to
 * general:header). Text colors inherit from the SectionStrip's CSS variables.
 */
export function VideoHeader({ breadcrumbs }: VideoHeaderProps = {}) {
    const { post, primaryCategory, locale: localeStr } = usePostData();
    const locale = localeStr as LocaleCode;
    const heroImage = post.heroImage as Media | null;

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const dayName = date.toLocaleDateString(locale, { weekday: "short" }).toUpperCase();
        const formattedDate = date.toLocaleDateString(locale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
        return `${dayName} ${formattedDate}`;
    };

    const categoryName = primaryCategory?.title?.toUpperCase() || "";

    return (
        <header className="relative w-full py-6 md:py-8">
            <div className="max-w-350 mx-auto px-4 md:px-6">
                {breadcrumbs ? <div className="pb-4 md:pb-6">{breadcrumbs}</div> : null}
                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                    {/* Left: Video embed or fallback hero image */}
                    <VideoEmbed
                        videoEmbed={post.videoEmbed}
                        heroImage={heroImage}
                        title={post.title}
                        className="md:w-1/2"
                        heroSize="large"
                        imageSizes="(max-width: 768px) 100vw, 50vw"
                        priority
                    />

                    {/* Right: Content */}
                    <div className="flex-1 p-6 md:p-8 lg:p-12 flex flex-col justify-center">
                        <div className="flex flex-col items-start md:flex-row md:items-center gap-3 mb-4">
                            {categoryName && (
                                <span className="inline-block px-3 py-1 text-xs font-bold rounded bg-foreground/20 text-inherit">
                                    {categoryName}
                                </span>
                            )}
                            {post.publishedAt && (
                                <span className="text-sm text-inherit opacity-70">
                                    {formatDate(post.publishedAt)}
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                            {post.title}
                        </h1>
                        {post.summary && (
                            <p className="text-base md:text-lg leading-relaxed text-inherit opacity-80">
                                {post.summary}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
